import { useMemo } from 'react';

import { SxProps } from '@cast/design-system';
import { Cluster } from '@cast/types';
import { KentInstallScriptOptions } from '@cast/types-kent';

import { ScriptSection } from 'hooks/dialogs/cluster/_components/new-onboarding';
import { useGetClusterInstallScriptQuery } from 'hooks/queries/kent/useGetClusterInstallScriptQuery';
import { useGetOrganizationInstallScriptQuery } from 'hooks/queries/kent/useGetOrganizationInstallScriptQuery';

import { SelectedFeatures } from '../InstallKentScript/_types';

type InstallClusterScriptProps = {
  cluster: Cluster;
  featuresToInstall: KentInstallScriptOptions;
  sx?: SxProps;
};

const InstallClusterScript = ({
  cluster,
  featuresToInstall,
  sx,
}: InstallClusterScriptProps) => {
  const {
    data: script,
    error,
    isLoading,
    refetch,
  } = useGetClusterInstallScriptQuery({
    requestData: {
      clusterId: cluster.id,
      installScriptOptions: featuresToInstall,
    },
  });

  return (
    <ScriptSection
      script={script}
      showReadOnlyAgentScriptDisclaimer={false}
      isLoading={isLoading}
      error={error}
      refetch={refetch}
      sx={sx}
    />
  );
};

type InstallOrganizationScriptProps = {
  featuresToInstall: KentInstallScriptOptions;
  sx?: SxProps;
};

const InstallOrganizationScript = ({
  featuresToInstall,
  sx,
}: InstallOrganizationScriptProps) => {
  const {
    data: script,
    error,
    isLoading,
    refetch,
  } = useGetOrganizationInstallScriptQuery({
    requestData: featuresToInstall,
  });

  return (
    <ScriptSection
      script={script}
      showReadOnlyAgentScriptDisclaimer={false}
      isLoading={isLoading}
      error={error}
      refetch={refetch}
      sx={sx}
    />
  );
};

const getFeaturesToInstall = ({
  selectedFeatures,
}: {
  selectedFeatures: SelectedFeatures;
}): KentInstallScriptOptions => {
  return {
    kubernetesAgent: true,
    podMutations: selectedFeatures.POD_MUTATIONS,
    rebalancer: selectedFeatures.REBALANCING,
    workloadRightSizing: selectedFeatures.WORKLOAD_RIGHT_SIZING,
    spotInterruptionPrediction: selectedFeatures.SPOT_INTERRUPTION_PREDICTION,
    clm: selectedFeatures.CLM,
  };
};

type Props = {
  cluster?: Cluster;
  selectedFeatures: SelectedFeatures;
  sx?: SxProps;
};

export const InstallScript = ({ cluster, selectedFeatures, sx }: Props) => {
  const featuresToInstall = useMemo(
    () =>
      getFeaturesToInstall({
        selectedFeatures,
      }),
    [selectedFeatures]
  );

  if (cluster) {
    return (
      <InstallClusterScript
        cluster={cluster}
        featuresToInstall={featuresToInstall}
        sx={sx}
      />
    );
  }

  return (
    <InstallOrganizationScript featuresToInstall={featuresToInstall} sx={sx} />
  );
};
